"use client";

import { TabBar } from "@/components/tab-bar";
import { useInfront } from "@/context/infront-context";
import {
  createInstrumentSlug,
  DEFAULT_CONTENT,
} from "@/services/infront/utils";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

type Chain = { feed: number; name: string; description: string };

type RankingListProps = {
  id: string;
  title?: string;
  sortOrder: number;
  limit?: number;
  chains: Chain[];
  compactMode?: boolean;
};

const RankingList = (props: RankingListProps) => {
  const { isReady, load } = useInfront();
  const router = useRouter();
  const widgetRef = useRef<any>(null);
  const [activeTab, setActiveTab] = useState<string>("");

  const activeChains = activeTab
    ? props.chains.filter((c) => c.description === activeTab)
    : props.chains.slice(0, 1);

  useEffect(() => {
    let active = true;

    load?.(["core"]).then(() => {
      if (!isReady || !active) return;

      widgetRef.current?.destroy();
      widgetRef.current = null;

      const widgetOptions = new window.Infront.QuoteListWidgetOptions();

      widgetOptions.onInstrumentSelected = (instrument: {
        feed: number;
        ticker: string;
      }) => {
        router.push(createInstrumentSlug(instrument.feed, instrument.ticker));
      };

      widgetOptions.useChains = true;
      widgetOptions.chains = activeChains;
      widgetOptions.showChainSelector = false;
      widgetOptions.defaultContent = DEFAULT_CONTENT;
      widgetOptions.widgetTitle = props.title;
      widgetOptions.sortable = true;
      widgetOptions.enableChangeStatusColors = true;
      widgetOptions.defaultSortedColumns = [
        {
          column: "PCT_CHANGE",
          sortOrder: props.sortOrder,
        },
      ];

      widgetOptions.instrumentTypes = ["STOCK"];
      widgetOptions.maxItems = props.limit || 12;
      widgetOptions.columns = props.compactMode
        ? [
            {
              name: "TICKER",
              flag: true,
            },
            "LAST",
            "PCT_CHANGE",
          ]
        : [
            {
              name: "TICKER",
              flag: true,
            },
            "LAST",
            "CURRENCY",
            "PCT_CHANGE",
          ];

      widgetRef.current = window.infront.quoteList(`#${props.id}`, widgetOptions);
    });

    return () => {
      active = false;
      widgetRef.current?.destroy();
      widgetRef.current = null;
    };
  }, [isReady, activeTab, props.sortOrder, props.id, props.title, props.limit, router]);

  const tabs = props.chains.map((c) => c.description);
  const selectedTab = activeTab || tabs[0];

  return (
    <div className="card [&_.cell-mininav__btn]:hidden">
      {tabs.length > 1 && (
        <TabBar
          tabs={tabs.map((t) => ({ label: t, value: t }))}
          active={selectedTab}
          onChange={setActiveTab}
        />
      )}
      <div id={props.id} />
    </div>
  );
};

export default RankingList;
